import {useLayoutTrackingRef as useTrackingRef} from '@github-ui/use-tracking-ref'
import {StackIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Banner, Checkbox, FormControl, Link} from '@primer/react'
import {useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState} from 'react'

import type {Repository} from '../../api/common-contracts'
import type {AutoAddEntities} from '../../api/memex/contracts'
import {getInitialState} from '../../helpers/initial-state'
import {validateBulkAddQuery} from '../../helpers/validate-bulk-add-query'
import {useAutoAddItemCount} from '../../state-providers/auto-add-item-count/auto-add-item-count-provider'
import {TemplateDialogResources} from '../../strings'
import {RepositoryPicker} from '../automation/repository-picker'
import {TokenizedFilterInput} from '../filter-bar/tokenized-filter-input'
import styles from './TemplateDialog.module.css'

function ServerErrorBanner({onDismiss}: {onDismiss: () => void}) {
  const bannerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bannerRef.current?.focus()
  }, [])

  return (
    <div ref={bannerRef} tabIndex={-1} className={styles.ErrorBanner} role="region" aria-label="Error notification">
      <Banner variant="critical" onDismiss={onDismiss} role="alert" aria-live="polite">
        <Banner.Title>Server error</Banner.Title>
        {TemplateDialogResources.serverErrorMessage}
      </Banner>
    </div>
  )
}

function RepositoryEntitiesPicker({
  entitiesText,
  onEntitiesChange,
  autoAddEnabledRepository,
  onRepositoryChange,
  currentEntities,
  templateLinkedRepositoryId,
}: {
  entitiesText: string
  onEntitiesChange: (entities: AutoAddEntities) => void
  autoAddEnabledRepository: Repository | null
  onRepositoryChange?: (repository?: Repository) => void
  currentEntities?: AutoAddEntities
  templateLinkedRepositoryId?: number
}) {
  const {refreshAutoAddItemCount} = useAutoAddItemCount()

  const handleEntitiesChange = useCallback(
    (entities: AutoAddEntities) => {
      onEntitiesChange(entities)

      if (autoAddEnabledRepository && entities.length > 0) {
        let additionalQuery = ''
        if (entities.length === 1) {
          additionalQuery = entities[0] === 'issues' ? 'is:issue' : 'is:pr'
        }
        const repoQuery = `${additionalQuery} is:open`
        refreshAutoAddItemCount({repositoryId: autoAddEnabledRepository.id, query: repoQuery})
      }
    },
    [onEntitiesChange, autoAddEnabledRepository, refreshAutoAddItemCount],
  )

  const handleRepositoryChange = useCallback(
    (repository?: Repository) => {
      onRepositoryChange?.(repository)

      if (repository && currentEntities && currentEntities.length > 0) {
        let additionalQuery = ''
        if (currentEntities.length === 1) {
          additionalQuery = currentEntities[0] === 'issues' ? 'is:issue' : 'is:pr'
        }
        const repoQuery = `${additionalQuery} is:open`
        refreshAutoAddItemCount({repositoryId: repository.id, query: repoQuery})
      }
    },
    [onRepositoryChange, currentEntities, refreshAutoAddItemCount],
  )

  const itemTypeLabelId = useId()
  const itemTypeValueId = useId()
  const repositoryLabelId = useId()

  return (
    <div className={styles.BulkAddConfigurationActions}>
      <div className={styles.BulkAddField}>
        <span id={itemTypeLabelId} className={styles.BulkAddFieldLabel}>
          {TemplateDialogResources.itemTypeLabel}
        </span>
        <ActionMenu>
          <ActionMenu.Button leadingVisual={StackIcon} aria-labelledby={`${itemTypeLabelId} ${itemTypeValueId}`}>
            <span id={itemTypeValueId}>{entitiesText}</span>
          </ActionMenu.Button>
          <ActionMenu.Overlay>
            <ActionList aria-label="Select entities to add">
              <ActionList.Item
                onSelect={() => {
                  handleEntitiesChange(['issues'])
                }}
              >
                {TemplateDialogResources.openIssues}
              </ActionList.Item>
              <ActionList.Item
                onSelect={() => {
                  handleEntitiesChange(['prs'])
                }}
              >
                {TemplateDialogResources.openPullRequests}
              </ActionList.Item>
              <ActionList.Item
                onSelect={() => {
                  handleEntitiesChange(['issues', 'prs'])
                }}
              >
                {TemplateDialogResources.openIssuesAndPullRequests}
              </ActionList.Item>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>
      <span className={styles.BulkAddFieldSeparator}>from</span>
      <div className={styles.BulkAddField}>
        <span id={repositoryLabelId} className={styles.BulkAddFieldLabel}>
          {TemplateDialogResources.repositoryLabel}
        </span>
        <RepositoryPicker
          targetRepositoryId={autoAddEnabledRepository?.id ?? templateLinkedRepositoryId}
          onRepositorySelected={handleRepositoryChange}
          isEditing
          shouldReset
          labelledById={repositoryLabelId}
        />
      </div>
    </div>
  )
}

const getInitialQuery = (entities: AutoAddEntities, defaultRepository?: Repository | null): string => {
  let entityFilter = ''
  if (entities.length === 1) {
    entityFilter = entities[0] === 'issues' ? 'is:issue' : 'is:pr'
  } else if (entities.length === 2) {
    entityFilter = '(is:issue OR is:pr)'
  }

  const repoFilter = defaultRepository ? `repo:${defaultRepository.nameWithOwner} ` : ''
  return `${entityFilter} state:open ${repoFilter}`.trim()
}

const BULK_ADD_CONTEXT = {
  searchType: 'global' as const,
}

/**
 * Validates search input using the pure validation helper.
 * Returns an error message if validation fails, undefined if valid.
 */
export const validateInput = (input: string): string | undefined => {
  const {isEnterpriseRuntime, isMultiTenantEnterprise} = getInitialState()

  // The validation function no longer requires user data and relies on environment flags
  const result = validateBulkAddQuery(input, {isEnterpriseRuntime, isMultiTenantEnterprise})

  return result.isValid ? undefined : result.error
}

function QueryPicker({
  autoAddEnabledRepository,
  onQueryChange,
  errorMessage,
  queryInputRef,
  onSyntaxValidation,
  currentEntities,
}: {
  autoAddEnabledRepository: Repository | null
  onQueryChange?: (query: string) => void
  errorMessage?: string | null
  queryInputRef?: React.RefObject<HTMLInputElement | null>
  onSyntaxValidation?: (errors: Array<string>) => void
  currentEntities: AutoAddEntities
}) {
  const initialQuery = useMemo(
    () => getInitialQuery(currentEntities, autoAddEnabledRepository),
    [currentEntities, autoAddEnabledRepository],
  )
  const [query, setQuery] = useState(initialQuery)
  const localInputRef = useRef<HTMLInputElement>(null)
  const inputRef = queryInputRef || localInputRef
  const {refreshAutoAddItemCount, resetAutoAddItemCount} = useAutoAddItemCount()

  const handleValidation = useCallback(
    (messages: Array<string>) => {
      onSyntaxValidation?.(messages)
    },
    [onSyntaxValidation],
  )

  useEffect(() => {
    onQueryChange?.(initialQuery)
    if (initialQuery.trim()) {
      refreshAutoAddItemCount({query: initialQuery})
    }
  }, [initialQuery, onQueryChange, refreshAutoAddItemCount])

  const handleQueryChange = useCallback(
    (newQuery: string) => {
      setQuery(newQuery)
      onQueryChange?.(newQuery)

      if (newQuery.trim()) {
        refreshAutoAddItemCount({query: newQuery})
      } else {
        resetAutoAddItemCount()
      }
    },
    [onQueryChange, refreshAutoAddItemCount, resetAutoAddItemCount],
  )

  return (
    <div className={styles.AdvancedSearchQueryInput}>
      <TokenizedFilterInput
        id="bulk-add-query-filter"
        value={query}
        onChangeValue={handleQueryChange}
        onValidation={handleValidation}
        formLabel="Import Query"
        inputRef={inputRef}
        hideSaveButton
        hideDisplayButton
        context={BULK_ADD_CONTEXT}
        groupAndKeywordSupport
      />
      {errorMessage && (
        <div role="alert" aria-live="polite">
          <FormControl.Validation variant="error">{errorMessage}</FormControl.Validation>
        </div>
      )}
    </div>
  )
}

export type SubmissionResult = 'success' | 'validation_error' | 'server_error'

/**
 * Exposed methods and state from the BulkAddConfiguration component
 */
export interface BulkAddConfigurationRef {
  /** Validates and submits the current query */
  validateQuery: () => Promise<SubmissionResult>
  /** Displays a server error banner to the user */
  showServerError: () => void
  /** Gets whether bulk add is currently enabled */
  isBulkAddEnabled: () => boolean
  /** Gets whether query mode is currently active (vs repository mode) */
  isQueryMode: () => boolean
}

/**
 * Props for the BulkAddConfiguration component
 */
export interface BulkAddConfigurationProps {
  ref?: React.Ref<BulkAddConfigurationRef>
  entitiesText: string
  onEntitiesChange: (entities: AutoAddEntities) => void
  autoAddEnabledRepository: Repository | null
  onRepositoryChange?: (repository?: Repository) => void
  onQueryChange?: (query: string) => void
  /** Callback to receive count from API preview */
  onQueryPreviewResult?: (count: number) => void
  currentQuery?: string
  /** Current selected entities for repository dropdown flow */
  currentEntities: AutoAddEntities
}

export const BulkAddConfiguration = ({
  ref,
  entitiesText,
  onEntitiesChange,
  autoAddEnabledRepository,
  onRepositoryChange,
  onQueryChange,
  onQueryPreviewResult: _onQueryPreviewResult,
  currentQuery,
  currentEntities,
}: BulkAddConfigurationProps) => {
  const {templateLinkedRepositoryId} = getInitialState()
  const [enableBulkAdd, setEnableBulkAdd] = useState(true)
  const [queryError, setQueryError] = useState<string | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [syntaxErrors, setSyntaxErrors] = useState<Array<string>>([])
  const queryInputRef = useRef<HTMLInputElement>(null)
  const serverErrorRef = useTrackingRef(serverError)
  const queryErrorRef = useTrackingRef(queryError)
  const syntaxErrorsRef = useTrackingRef(syntaxErrors)

  const handleSyntaxValidation = useCallback((errors: Array<string>) => {
    setSyntaxErrors(errors)
  }, [])

  const [useQueryMode, setUseQueryMode] = useState(false)
  const {resetAutoAddItemCount} = useAutoAddItemCount()

  const handleToggleMode = useCallback(() => {
    setUseQueryMode(prev => !prev)
    setQueryError(null)
    setServerError(null)
    resetAutoAddItemCount()
  }, [resetAutoAddItemCount])

  const handleQueryChange = useCallback(
    (newQuery: string) => {
      if (queryErrorRef.current) {
        setQueryError(null)
      }
      if (serverErrorRef.current) {
        setServerError(null)
      }
      onQueryChange?.(newQuery)
    },
    [onQueryChange, queryErrorRef, serverErrorRef],
  )

  const validateQuery = useCallback(async (): Promise<SubmissionResult> => {
    if (!enableBulkAdd) {
      return 'success'
    }

    if (!currentQuery?.trim()) {
      setQueryError(TemplateDialogResources.invalidNoQuery)
      queryInputRef.current?.focus()
      return 'validation_error'
    }

    setQueryError(null)
    setServerError(null)

    const validationError = validateInput(currentQuery)
    if (validationError) {
      setQueryError(validationError)
      queryInputRef.current?.focus()
      return 'validation_error'
    }

    if (syntaxErrorsRef.current.length > 0) {
      // Do not setQueryError() as syntax errors are handled in TokenizedFilterInput
      // Do not focus() as TokenizedFilterInput hides validation when focused
      return 'validation_error'
    }

    return 'success'
  }, [enableBulkAdd, currentQuery, syntaxErrorsRef])

  const showServerError = useCallback(() => {
    setServerError(TemplateDialogResources.serverErrorMessage)
  }, [])

  const isBulkAddEnabled = useCallback(() => {
    return enableBulkAdd
  }, [enableBulkAdd])

  const isQueryModeEnabled = useCallback(() => {
    return useQueryMode
  }, [useQueryMode])

  useImperativeHandle(
    ref,
    () => ({
      validateQuery,
      showServerError,
      isBulkAddEnabled,
      isQueryMode: isQueryModeEnabled,
    }),
    [validateQuery, showServerError, isBulkAddEnabled, isQueryModeEnabled],
  )

  const onEnableBulkAddChange = useCallback(() => {
    const newEnableBulkAdd = !enableBulkAdd
    setEnableBulkAdd(newEnableBulkAdd)
    if (!newEnableBulkAdd) {
      onRepositoryChange?.(undefined)
      resetAutoAddItemCount()
    }
  }, [enableBulkAdd, setEnableBulkAdd, onRepositoryChange, resetAutoAddItemCount])

  return (
    <div className={styles.BulkAddConfigurationContainer}>
      {serverError && <ServerErrorBanner onDismiss={() => setServerError(null)} />}

      <FormControl>
        <Checkbox
          checked={enableBulkAdd}
          onChange={onEnableBulkAddChange}
          aria-describedby="bulk-add-description"
          data-testid="bulk-add-checkbox"
        />
        <FormControl.Label className={styles.formControlLabel}>
          {useQueryMode ? TemplateDialogResources.bulkAddSearch : TemplateDialogResources.bulkAddRepository}
        </FormControl.Label>
        <FormControl.Caption id="bulk-add-description">
          {useQueryMode
            ? TemplateDialogResources.bulkAddSubtitleSearch
            : TemplateDialogResources.bulkAddSubtitleRepository}
        </FormControl.Caption>
      </FormControl>

      {enableBulkAdd && (
        <>
          {useQueryMode ? (
            <QueryPicker
              autoAddEnabledRepository={autoAddEnabledRepository}
              onQueryChange={handleQueryChange}
              onSyntaxValidation={handleSyntaxValidation}
              errorMessage={queryError}
              queryInputRef={queryInputRef}
              currentEntities={currentEntities}
            />
          ) : (
            <RepositoryEntitiesPicker
              entitiesText={entitiesText}
              onEntitiesChange={onEntitiesChange}
              autoAddEnabledRepository={autoAddEnabledRepository}
              onRepositoryChange={onRepositoryChange}
              currentEntities={currentEntities}
              templateLinkedRepositoryId={templateLinkedRepositoryId}
            />
          )}
          <p className={styles.ModeToggleLink}>
            Add from a{' '}
            <Link as="button" inline onClick={handleToggleMode}>
              {useQueryMode ? 'repository' : 'search query'}{' '}
            </Link>{' '}
            instead
          </p>
        </>
      )}
    </div>
  )
}

BulkAddConfiguration.displayName = 'BulkAddConfiguration'
